/** Drawer „Talent-Vorschau“ (Schritt 4): Bewertung, Persona, Kriterien, Profil, Unternehmen, Blacklist-Aktionen. */

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DomainDisabledIcon from '@mui/icons-material/DomainDisabled';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { getCandidate, WizardApiError } from '../../campaign-wizard.api';
import {
  Judgement,
  WizardBlacklistList,
  WizardCandidate,
  WizardCandidateDetail,
  WizardState,
} from '../../campaign-wizard.types';
import { MiSpinner } from '../../components/wizard-ui';
import { wz } from '../../components/wizard-tokens';
import {
  blacklistTargets,
  cycleId,
  drawerPosition,
  initialsOf,
  JUDGE_COLORS,
  ratingPill,
  safeLinkedInUrl,
  JUDGE_LABEL_KEYS,
  JUDGEMENTS,
  MenuTarget,
} from './candidate-list.logic';
import PersonaCard from './persona-card';

type DrawerTab = 0 | 1 | 2;
type MenuKind = 'judge' | 'person' | 'company' | null;

interface Props {
  campaignId: string;
  candidateId: string;
  /** Kandidaten der gefilterten Liste, in Anzeigereihenfolge (Blättern). */
  items: WizardCandidate[];
  state: WizardState;
  blacklists: WizardBlacklistList[];
  onNavigate: (id: string) => void;
  onClose: () => void;
  /** Fehler der letzten Aktion (z. B. 409 bei Blacklist) – wird im Drawer angezeigt. */
  actionError?: string | null;
  onSetActive: (candidate: WizardCandidate, active: boolean) => Promise<void>;
  onSetJudgement: (candidate: WizardCandidate, judgement: Judgement | null) => Promise<boolean>;
  onRejudge?: (candidate: WizardCandidate) => Promise<boolean>;
  onAddToBlacklist: (
    candidate: WizardCandidate,
    scope: 'person' | 'company',
    target: string
  ) => Promise<boolean>;
  onRemoveFromBlacklist: (
    candidate: WizardCandidate,
    scope: 'person' | 'company'
  ) => Promise<boolean>;
}

const CandidateDrawer: React.FC<Props> = ({
  campaignId,
  candidateId,
  items,
  state,
  blacklists,
  onNavigate,
  onClose,
  actionError,
  onSetActive,
  onSetJudgement,
  onRejudge,
  onAddToBlacklist,
  onRemoveFromBlacklist,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [tab, setTab] = useState<DrawerTab>(0);
  const [rejudging, setRejudging] = useState(false);
  const [rejudgeFailed, setRejudgeFailed] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    panelRef.current?.focus();
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const [menu, setMenu] = useState<MenuKind>(null);
  const [detail, setDetail] = useState<WizardCandidateDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  const listed = items.find(item => item.id === candidateId) ?? null;
  const cand: WizardCandidate | null = listed ?? detail;
  const ids = useMemo(() => items.map(item => item.id), [items]);
  const pos = drawerPosition(ids, candidateId);

  // Details laden, wenn der Kandidat wechselt oder sich Urteil/Blockade ändern.
  const signature = cand
    ? [cand.judgement, cand.manual, cand.blockedBy.join('|'), cand.personBlockedBy.join('|')].join(
        '#'
      )
    : '';
  useEffect(() => {
    let cancelled = false;
    setDetailError(null);
    getCandidate(campaignId, candidateId)
      .then(res => !cancelled && setDetail(res))
      .catch(e => !cancelled && setDetailError((e as WizardApiError).message));
    return () => {
      cancelled = true;
    };
  }, [campaignId, candidateId, signature]);

  useEffect(() => {
    setMenu(null);
    setTab(0);
  }, [candidateId]);

  const current = detail && detail.id === candidateId ? detail : null;

  const judgeLabel = (j: Judgement | null) =>
    j ? t(JUDGE_LABEL_KEYS[j]) : t('wizListJudgePending');

  const personBlocked = (cand?.personBlockedBy ?? []).length > 0;
  const companyBlocked = (cand?.companyBlockedBy ?? []).length > 0;

  const targetText = (target: MenuTarget) => {
    if (target.kind === 'campaign') {
      return { name: t('wizDrawerTargetCampaign'), meta: t('wizDrawerTargetCampaignMeta') };
    }
    const base = t(
      target.listType === 'company'
        ? 'wizDrawerTargetGlobalCompanies'
        : 'wizDrawerTargetGlobalContacts',
      { amount: target.count ?? 0 }
    );
    return { name: target.name, meta: base + (target.off ? t('wizDrawerTargetOff') : '') };
  };

  const blacklistButton = (scope: 'person' | 'company') => {
    if (!cand) return null;
    const blocked = scope === 'person' ? personBlocked : companyBlocked;
    const kind: MenuKind = scope;
    const label =
      scope === 'person'
        ? t(blocked ? 'wizDrawerPersonRemove' : 'wizDrawerPersonAdd')
        : t(blocked ? 'wizDrawerCompanyRemove' : 'wizDrawerCompanyAdd', { company: cand.company });
    const targets = blacklistTargets(scope, blacklists, state.globalBlacklists);
    return (
      <div className={classes.menuAnchor}>
        <button
          type="button"
          className={classes.blButton}
          onClick={() => {
            if (blocked) void onRemoveFromBlacklist(cand, scope);
            else setMenu(menu === kind ? null : kind);
          }}
        >
          {scope === 'person' ? (
            <PersonOffIcon sx={{ fontSize: 16 }} />
          ) : (
            <DomainDisabledIcon sx={{ fontSize: 16 }} />
          )}
          {label}
        </button>
        {menu === kind ? (
          <div className={classes.menu} style={{ top: 26, left: 0, width: 300, zIndex: 4 }}>
            <div className={classes.menuTitle}>
              {t('wizDrawerAddTo', { name: scope === 'person' ? cand.name : cand.company })}
            </div>
            {targets.map(target => {
              const text = targetText(target);
              return (
                <button
                  key={target.target}
                  type="button"
                  className={classes.menuItemCol}
                  onClick={() => {
                    setMenu(null);
                    void onAddToBlacklist(cand, scope, target.target);
                  }}
                >
                  <div style={{ fontSize: 14 }}>{text.name}</div>
                  <div style={{ fontSize: 12, color: wz.muted }}>{text.meta}</div>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  };

  const blockedText = cand
    ? personBlocked
      ? t('wizDrawerBlockedPerson', { lists: cand.personBlockedBy.join(', ') })
      : companyBlocked
        ? t('wizDrawerBlockedCompany', {
            company: cand.company,
            lists: cand.companyBlockedBy.join(', '),
          })
        : ''
    : '';

  /** Kriterien der Kampagne – nur zur Anzeige. Die KI vergibt eine Klasse mit Begründung, keine Einzelurteile. */
  const criteriaGroups = current
    ? (
        [
          { key: 'must', label: t('wizDrawerGroupMust') },
          { key: 'nice', label: t('wizDrawerGroupNice') },
          { key: 'excl', label: t('wizDrawerGroupExcl') },
        ] as const
      ).flatMap(group =>
        (current.criteria?.[group.key] ?? []).map(text => ({ group: group.label, text }))
      )
    : [];

  const tabs = ['wizDrawerTabOverview', 'wizDrawerTabCandidate', 'wizDrawerTabCompany'];

  return (
    <div
      className={classes.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={t('wizDrawerTitle')}
    >
      <div className={classes.backdrop} onClick={onClose} aria-hidden />
      <div className={classes.panel} ref={panelRef} tabIndex={-1}>
        <div className={classes.root}>
          {menu ? (
            <div className={classes.clickAway} onClick={() => setMenu(null)} aria-hidden />
          ) : null}
          <div className={classes.head}>
            <div className={classes.headRow}>
              <div className={classes.headLeft}>
                <button
                  type="button"
                  className={classes.iconBtn}
                  onClick={() => {
                    const next = cycleId(ids, candidateId, -1);
                    if (next) onNavigate(next);
                  }}
                  aria-label={t('wizDrawerPrev')}
                >
                  <ArrowBackIcon sx={{ fontSize: 22, color: wz.primary }} />
                </button>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{t('wizDrawerTitle')}</div>
                <div style={{ fontSize: 15 }}>
                  {pos >= 0 ? `(${pos + 1} / ${items.length})` : ''}
                </div>
                <button
                  type="button"
                  className={classes.iconBtn}
                  onClick={() => {
                    const next = cycleId(ids, candidateId, 1);
                    if (next) onNavigate(next);
                  }}
                  aria-label={t('wizDrawerNext')}
                >
                  <ArrowForwardIcon sx={{ fontSize: 22, color: wz.primary }} />
                </button>
              </div>
              <div className={classes.headRight}>
                <button
                  type="button"
                  className={classes.iconBtn}
                  onClick={onClose}
                  aria-label={t('wizDrawerClose')}
                >
                  <CloseIcon sx={{ fontSize: 22, color: wz.text555 }} />
                </button>
              </div>
            </div>

            {cand ? (
              <>
                <div className={classes.person}>
                  <div className={classes.avatar}>{current?.initials ?? initialsOf(cand.name)}</div>
                  <div className={classes.personText}>
                    <div style={{ fontSize: 17, fontWeight: 700 }}>{cand.name}</div>
                    {safeLinkedInUrl(cand.linkedInUrl) ? (
                      <a
                        className={classes.profileLink}
                        href={safeLinkedInUrl(cand.linkedInUrl) as string}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('wizDrawerProfileLink')}
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </a>
                    ) : null}
                    <div style={{ fontSize: 14, color: wz.text555 }}>
                      {t('wizDrawerAt', { title: cand.title, company: cand.company })}
                    </div>
                    <div style={{ fontSize: 13, color: wz.muted }}>
                      {current?.yearsOfExperience !== null &&
                      current?.yearsOfExperience !== undefined
                        ? t('wizDrawerYears', {
                            location: cand.location,
                            years: current.yearsOfExperience,
                          })
                        : cand.location}
                    </div>
                  </div>
                  <div className={classes.judgeBox}>
                    <button
                      type="button"
                      className={classes.judgePill}
                      style={{
                        background: ratingPill(cand).background,
                        color: ratingPill(cand).color,
                      }}
                      onClick={() => setMenu(menu === 'judge' ? null : 'judge')}
                    >
                      {t(ratingPill(cand).labelKey)}
                      <ExpandMoreIcon sx={{ fontSize: 18 }} />
                    </button>
                    {cand.manual ? (
                      <div className={classes.manual}>
                        {t('wizDrawerManual')}
                        <button
                          type="button"
                          className={classes.resetLink}
                          onClick={() => void onSetJudgement(cand, null)}
                        >
                          {t('wizDrawerOrigJudge', { label: judgeLabel(cand.aiJudgement) })}
                        </button>
                      </div>
                    ) : null}
                    {menu === 'judge' ? (
                      <div
                        className={classes.menu}
                        style={{ top: 30, right: 0, width: 220, zIndex: 3 }}
                      >
                        <div className={classes.menuTitle}>{t('wizDrawerChangeJudgement')}</div>
                        {JUDGEMENTS.map(j => (
                          <button
                            key={j}
                            type="button"
                            className={classes.menuItemRow}
                            onClick={() => {
                              setMenu(null);
                              void onSetJudgement(cand, j === cand.aiJudgement ? null : j);
                            }}
                          >
                            <span className={classes.dot} style={{ background: JUDGE_COLORS[j] }} />
                            <div style={{ flex: 1, textAlign: 'left' }}>
                              {t(JUDGE_LABEL_KEYS[j])}
                            </div>
                            {cand.judgement === j ? (
                              <CheckIcon sx={{ fontSize: 18, color: wz.primary }} />
                            ) : null}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>

                {blockedText ? (
                  <div className={classes.blocked}>
                    <DoNotDisturbIcon sx={{ fontSize: 16 }} />
                    {blockedText}
                  </div>
                ) : null}

                <div className={classes.toggle}>
                  <button
                    type="button"
                    className={classes.toggleHalf}
                    style={{
                      background: !cand.active ? '#F60404' : '#FFF',
                      color: !cand.active ? '#FFF' : wz.muted,
                    }}
                    onClick={() => cand.active && void onSetActive(cand, false)}
                  >
                    {t('wizDrawerExclude')}
                  </button>
                  <button
                    type="button"
                    className={classes.toggleHalf}
                    style={{
                      background: cand.active ? '#18C61F' : '#FFF',
                      color: cand.active ? '#FFF' : wz.muted,
                      borderLeft: `1px solid ${wz.border}`,
                    }}
                    onClick={() => !cand.active && void onSetActive(cand, true)}
                  >
                    {t('wizDrawerInclude')}
                  </button>
                </div>
                {actionError ? (
                  <div style={{ fontSize: 13, color: wz.redFg }} role="alert">
                    {t('wizListActionError')} {actionError}
                  </div>
                ) : null}

                <div className={classes.blRow}>
                  {blacklistButton('person')}
                  {blacklistButton('company')}
                </div>
              </>
            ) : null}

            <div className={classes.tabs}>
              {tabs.map((key, i) => (
                <button
                  key={key}
                  type="button"
                  className={classes.tab}
                  style={{
                    borderBottom: tab === i ? `2px solid ${wz.primary}` : '2px solid transparent',
                    color: tab === i ? wz.primary : wz.muted,
                    fontWeight: tab === i ? 700 : 400,
                  }}
                  onClick={() => setTab(i as DrawerTab)}
                >
                  {t(key)}
                </button>
              ))}
            </div>
          </div>

          <div className={classes.body}>
            {detailError ? (
              <div className={classes.error}>
                {t('wizDrawerLoadError')} {detailError}
              </div>
            ) : null}
            {!current && !detailError ? (
              <div className={classes.loading}>
                <MiSpinner size={20} />
                {t('wizDrawerLoading')}
              </div>
            ) : null}

            {current && cand && tab === 0 ? (
              <>
                <div className={classes.heroBorder}>
                  <div className={classes.hero}>
                    <div
                      className={classes.ring}
                      style={{
                        background: cand.judgement ? JUDGE_COLORS[cand.judgement] : wz.border,
                      }}
                      aria-hidden="true"
                    >
                      <div className={classes.classLetter}>{cand.judgement ?? '–'}</div>
                    </div>
                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                      }}
                    >
                      <div className={classes.eyebrow}>
                        <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                        {t('wizDrawerAssessment')}
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 700 }}>
                        {t(ratingPill(cand).labelKey)}
                      </div>
                      {cand.judgement ? (
                        <div style={{ fontSize: 13, color: wz.muted }}>
                          {t(`wizJudgClass${cand.judgement}Desc`)}
                        </div>
                      ) : null}
                      {!cand.judgement && ratingPill(cand).hintKey ? (
                        <div style={{ fontSize: 13, color: wz.textDim }} data-testid="rating-hint">
                          {ratingPill(cand).hintText ?? t(ratingPill(cand).hintKey as string)}
                          {ratingPill(cand).canRejudge && onRejudge ? (
                            <button
                              type="button"
                              className={classes.resetLink}
                              style={{ marginLeft: 8 }}
                              disabled={rejudging}
                              onClick={() => {
                                if (rejudging) return;
                                setRejudging(true);
                                setRejudgeFailed(false);
                                void onRejudge(cand)
                                  .then(ok => setRejudgeFailed(!ok))
                                  .finally(() => setRejudging(false));
                              }}
                            >
                              {t('wizDrawerRejudge')}
                            </button>
                          ) : null}
                          {rejudgeFailed ? (
                            <div
                              style={{ color: wz.redFg }}
                              role="alert"
                              data-testid="rejudge-error"
                            >
                              {t('wizDrawerRejudgeError')} {actionError}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                      <div style={{ fontSize: 13, lineHeight: 1.5, color: wz.textDim }}>
                        {current.why}
                      </div>
                    </div>
                  </div>
                </div>

                <PersonaCard persona={current.persona} firstName={cand.firstName} />

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: wz.text555 }}>
                    {t('wizDrawerSkillMatch')}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {current.skills.map(s => (
                      <div
                        key={s.label}
                        className={classes.skill}
                        style={{
                          background: s.hit ? wz.gradient : wz.page,
                          color: s.hit ? '#FFF' : wz.textDim,
                        }}
                      >
                        {s.hit ? <AutoAwesomeIcon sx={{ fontSize: 13 }} /> : null}
                        {s.label}
                      </div>
                    ))}
                  </div>
                </div>

                {criteriaGroups.length > 0 ? (
                  <div className={classes.checks}>
                    <div style={{ fontSize: 12, color: wz.muted, padding: '6px 0' }}>
                      {t('wizDrawerCriteria')}
                    </div>
                    {criteriaGroups.map(c => (
                      <div key={`${c.group}-${c.text}`} className={classes.checkRow}>
                        <div style={{ width: 100, color: wz.muted }}>{c.group}</div>
                        <div style={{ flex: 1 }}>{c.text}</div>
                      </div>
                    ))}
                    <div style={{ fontSize: 12, color: wz.muted, padding: '6px 0' }}>
                      {t('wizDrawerCriteriaNote')}
                    </div>
                  </div>
                ) : null}
                <div className={classes.foot}>
                  <AutoAwesomeIcon sx={{ fontSize: 14, color: wz.primary }} />
                  {t('wizDrawerAnalysed')}
                </div>
              </>
            ) : null}

            {current && tab === 1 ? (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{t('wizDrawerExperience')}</div>
                  {current.experience.map(e => (
                    <div key={`${e.role}-${e.at}-${e.when}`} className={classes.expRow}>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{e.role}</div>
                      <div style={{ fontSize: 13, color: wz.muted }}>
                        {e.at} · {e.when}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{t('wizDrawerSkills')}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {current.skills.map(s => (
                      <div key={s.label} className={classes.skillPlain}>
                        {s.label}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : null}

            {current && tab === 2 ? (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {[
                  { k: t('wizDrawerCompanyName'), v: current.companyInfo.name },
                  { k: t('wizDrawerIndustry'), v: current.companyInfo.industry },
                  {
                    k: t('wizDrawerSize'),
                    v: t('wizDrawerSizeValue', { size: current.companyInfo.size }),
                  },
                  { k: t('wizDrawerLocation'), v: current.companyInfo.location },
                ].map(row => (
                  <div key={row.k} className={classes.companyRow}>
                    <div style={{ width: 120, color: wz.muted }}>{row.k}</div>
                    <div>{row.v}</div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles()({
  // Innerhalb der Inhaltskarte des Wizards (Design: position:absolute; inset:0).
  overlay: {
    position: 'absolute',
    inset: 0,
    zIndex: 40,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  backdrop: { position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.2)' },
  panel: {
    position: 'relative',
    width: 540,
    maxWidth: '100%',
    height: '100%',
    background: '#FFF',
    boxShadow: '5px 5px 40px 0 rgba(0,0,0,0.16)',
    outline: 'none',
    overflow: 'hidden',
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'relative',
    fontFamily: 'Roboto, Arial, sans-serif',
    color: wz.text,
  },
  clickAway: { position: 'fixed', inset: 0, zIndex: 2 },
  head: {
    padding: '16px 20px',
    borderBottom: `1px solid ${wz.border}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  headRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  headLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  headRight: { display: 'flex', alignItems: 'center', gap: 14 },
  iconBtn: {
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
  },
  error: { fontSize: 13, color: wz.redFg },
  person: { display: 'flex', alignItems: 'center', gap: 14 },
  avatar: {
    width: 52,
    height: 52,
    borderRadius: '50%',
    background: wz.primarySoft,
    color: wz.primary,
    fontWeight: 700,
    fontSize: 17,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  profileLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 13,
    fontWeight: 600,
    color: wz.primary,
    textDecoration: 'none',
    width: 'fit-content',
    '&:hover': { textDecoration: 'underline' },
  },
  personText: { flex: 1, display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 },
  judgeBox: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 4,
  },
  judgePill: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    borderRadius: 10,
    padding: '2px 4px 2px 10px',
    fontSize: 13,
    cursor: 'pointer',
    border: 'none',
    fontFamily: 'inherit',
    color: wz.text,
  },
  manual: { fontSize: 11, color: wz.muted, display: 'flex', alignItems: 'center', gap: 4 },
  resetLink: {
    color: wz.primary,
    textDecoration: 'underline',
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    padding: 0,
    fontSize: 11,
    fontFamily: 'inherit',
  },
  menuAnchor: { position: 'relative' },
  menu: {
    position: 'absolute',
    background: '#FFF',
    borderRadius: 10,
    boxShadow: wz.shadowMenu,
    padding: 6,
    display: 'flex',
    flexDirection: 'column',
  },
  menuTitle: { fontSize: 12, color: wz.muted, padding: '6px 10px' },
  menuItemRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    background: 'transparent',
    border: 'none',
    fontFamily: 'inherit',
    color: wz.text,
    '&:hover': { background: wz.page },
  },
  menuItemCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
    padding: '8px 10px',
    borderRadius: 8,
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    fontFamily: 'inherit',
    color: wz.text,
    textAlign: 'left',
    '&:hover': { background: wz.page },
  },
  dot: { width: 10, height: 10, borderRadius: '50%', display: 'inline-block' },
  blocked: {
    fontSize: 13,
    color: wz.redFg,
    background: wz.redBg,
    borderRadius: 8,
    padding: '8px 10px',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  toggle: {
    display: 'flex',
    border: `1px solid ${wz.border}`,
    borderRadius: 8,
    overflow: 'hidden',
  },
  toggleHalf: {
    flex: 1,
    textAlign: 'center',
    padding: 8,
    fontSize: 13,
    cursor: 'pointer',
    border: 'none',
    fontFamily: 'inherit',
  },
  blRow: { display: 'flex', gap: 16, flexWrap: 'wrap' },
  blButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    color: wz.text555,
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    padding: 0,
    fontFamily: 'inherit',
    '&:hover': { color: '#F60404' },
  },
  tabs: { display: 'flex', gap: 18, marginBottom: -15 },
  tab: {
    padding: '6px 0 10px',
    fontSize: 13,
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    borderRadius: 0,
    fontFamily: 'inherit',
  },
  body: {
    flex: 1,
    overflow: 'auto',
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  loading: { display: 'flex', alignItems: 'center', gap: 8, color: wz.muted, fontSize: 14 },
  heroBorder: {
    borderRadius: 14,
    padding: 1.5,
    background: 'linear-gradient(135deg,#4F44FF,#007FFF)',
  },
  hero: {
    borderRadius: 13,
    background: 'linear-gradient(135deg,#F8F8FF,#FFFFFF 60%)',
    padding: 18,
    display: 'flex',
    gap: 18,
    alignItems: 'center',
  },
  ring: {
    width: 96,
    height: 96,
    borderRadius: '50%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  classLetter: { fontSize: 36, fontWeight: 700, color: wz.text },
  eyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: wz.primary,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  skill: {
    fontSize: 13,
    borderRadius: 14,
    padding: '4px 10px',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  skillPlain: { fontSize: 13, background: wz.page, borderRadius: 14, padding: '4px 10px' },
  checks: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${wz.border}`,
    borderRadius: 10,
    padding: '6px 14px',
  },
  checkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '6px 0',
    borderTop: `1px solid ${wz.page}`,
    fontSize: 13,
  },
  foot: { fontSize: 12, color: wz.muted, display: 'flex', alignItems: 'center', gap: 4 },
  expRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: '8px 0',
    borderBottom: `1px solid ${wz.page}`,
  },
  companyRow: {
    display: 'flex',
    gap: 12,
    padding: '10px 0',
    borderBottom: `1px solid ${wz.page}`,
    fontSize: 14,
  },
});

export default CandidateDrawer;
